import { QueryBuilder, migrate } from 'vet-bones/utils/queryBuilder';

import { Project, ProjectModel, ProjectsState } from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';

type RuleWithClauses = {
  ifClause: QueryBuilder | null;
  thenClause: QueryBuilder | null;
};

/**
 * Run the Query Builder migration over a map of rules. Text rules carry `null`
 * clauses and are left as-is.
 */
const migrateRulesById = <Rule extends RuleWithClauses>(
  rulesById: Record<string, Rule>
) =>
  Object.entries(rulesById).reduce<Record<string, Rule>>(
    (acc, [ruleId, rule]) => {
      acc[ruleId] = {
        ...rule,
        ifClause: rule.ifClause ? migrate(rule.ifClause) : null,
        thenClause: rule.thenClause ? migrate(rule.thenClause) : null,
      };
      return acc;
    },
    {}
  );

/**
 * Migration to upgrade the rules that `v52` missed to Query Builder v6.
 *
 * `v52` only covered `diagram.model.modelData.rulesById`, but rules are also
 * held in `modelsById[modelId].rules` (copied into the diagram by
 * `addProjectModelsToDiagramModel`) and in the draft snapshot at
 * `modelData.draftData.prevModelData.rulesById` (restored wholesale when a
 * draft is discarded). Both were left at the old version.
 */
// prevState must be typed as `any` since it's an older version of the Redux State
export const v53 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v53Projects(prevState.projects);
  }

  return nextState;
};

const v53Projects = (prevProjectsState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, Project>
  ).forEach(([projectId, project]) => {
    const modelsById = project.modelsById
      ? Object.entries(project.modelsById).reduce<Record<string, ProjectModel>>(
          (acc, [modelId, model]) => {
            acc[modelId] = model.rules
              ? { ...model, rules: migrateRulesById(model.rules) }
              : model;
            return acc;
          },
          {}
        )
      : project.modelsById;

    const { draftData } = project.diagram?.model?.modelData ?? {};
    // a draft snapshot may predate `rulesById`, so only rebuild when it exists
    const diagram = draftData?.prevModelData?.rulesById
      ? {
          ...project.diagram,
          model: {
            ...project.diagram.model,
            modelData: {
              ...project.diagram.model.modelData,
              draftData: {
                ...draftData,
                prevModelData: {
                  ...draftData.prevModelData,
                  rulesById: migrateRulesById(
                    draftData.prevModelData.rulesById
                  ),
                },
              },
            },
          },
        }
      : project.diagram;

    projectsById[projectId] = { ...project, modelsById, diagram };
  });

  return { ...prevProjectsState, projectsById };
};
